import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MessageCircle, ArrowUpRight, ChevronDown } from 'lucide-react';

const INTENTS = [
  {
    key: 'nomination',
    label: 'Nominate',
    sub: 'Submit an entry',
    msg: "Hi, I'd like to nominate at AdAura Awards 2026.",
  },
  {
    key: 'sponsorship',
    label: 'Sponsorship',
    sub: 'Partner with us',
    msg: "Hi, I'd like to explore sponsorship opportunities at AdAura Awards 2026.",
  },
  {
    key: 'speakership',
    label: 'Speakership',
    sub: 'Speak / join the jury',
    msg: "Hi, I'd like to discuss speakership or jury opportunities at AdAura Awards 2026.",
  },
];

const WhatsAppPicker = ({ phone, testId = 'whatsapp-picker' }) => {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const [entered, setEntered] = useState(false);
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const cleanPhone = phone.replace(/[^0-9]/g, '');

  // Compute the trigger rect SYNCHRONOUSLY the moment `open` flips to true —
  // before the portal renders — so the menu appears at the correct position
  // on the first paint and never flickers/jumps.
  useLayoutEffect(() => {
    if (open && triggerRef.current) {
      setRect(triggerRef.current.getBoundingClientRect());
    } else {
      setRect(null);
      setEntered(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Fire the entry animation on the next frame so the initial `opacity-0
    // translate-y-1` state paints first, then transitions to the visible
    // state — no flash of unstyled dropdown.
    const raf = requestAnimationFrame(() => setEntered(true));

    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onScrollOrResize = () => {
      if (triggerRef.current) setRect(triggerRef.current.getBoundingClientRect());
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative" data-testid={testId}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testId}-trigger`}
        aria-expanded={open}
        className="group flex items-center gap-4 text-white/85 hover:text-[#C9A56C] transition-colors text-base md:text-lg border-t border-[#C9A56C]/30 pt-6 w-full text-left"
      >
        <MessageCircle className="w-5 h-5 text-[#C9A56C]" />
        <span>WhatsApp Us</span>
        <ChevronDown
          className={`w-4 h-4 ml-auto text-[#C9A56C] transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && rect && createPortal(
        (
          <div
            style={{
              position: 'fixed',
              top: rect.bottom + 8,
              left: rect.left,
              width: Math.min(rect.width, 320),
              zIndex: 9999,
            }}
            className={`bg-[#463724] border border-[#C9A56C]/30 shadow-[0_20px_50px_rgba(42,26,15,0.35)] origin-top transition-[opacity,transform] duration-150 ease-out ${entered ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'}`}
            data-testid={`${testId}-menu`}
          >
            <div className="px-5 py-3 border-b border-[#C9A56C]/15">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#C9A56C]">Choose intent</div>
            </div>
            {INTENTS.map((it, i) => (
              <a
                key={it.key}
                href={`https://wa.me/${cleanPhone}?text=${encodeURIComponent(it.msg)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid={`${testId}-${it.key}`}
                className={`group flex items-center gap-4 px-5 py-4 hover:bg-[#634F3A] transition-colors ${
                  i < INTENTS.length - 1 ? 'border-b border-[#C9A56C]/12' : ''
                }`}
              >
                <span className="numeral text-[#C9A56C]/70 text-xs w-6">{String(i + 1).padStart(2, '0')}</span>
                <div className="flex-1">
                  <div className="font-serif text-white text-[15px] leading-tight">{it.label}</div>
                  <div className="text-white/55 text-[12px] mt-0.5">{it.sub}</div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-[#C9A56C] opacity-60 group-hover:opacity-100 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
              </a>
            ))}
          </div>
        ),
        document.body,
      )}
    </div>
  );
};

export default WhatsAppPicker;
